import { NgTemplateOutlet } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input, OnChanges } from '@angular/core';

import { MdBlock, parseAgentsMarkdown } from './agents-markdown.parser';

/**
 * Draws a bot's reply (Channels C0b, AB#20876, design 3.1 and 3.3 step 5) from the blocks {@link parseAgentsMarkdown}
 * reads. Every word reaches the page through an Angular text binding and every link through a bound href that
 * `safeLinkHref` already limited to https and same-origin paths. No HTML string is ever bound, and there is no sanitizer
 * call or bypass, so a reply cannot put a script, an event handler, an iframe or an image on the page. The blocks are
 * built once per input change, never from the template.
 *
 * Standalone: it is wired into the conversation later (P3, P5).
 *
 * Type checking: until P3/P5 import it, nothing reachable from `src/main.ts` imports this folder, so the repo gate
 * `tsc -p tsconfig.app.json` does not see it, and its spec run only transpiles (vitest does not type-check). Until
 * then, a change to this folder is type-checked with its spec run plus an ngc compile of this component under
 * `strictTemplates` (a scratch tsconfig extending tsconfig.json with this file as its only entry, which checks the
 * parser and the template too). Once it is imported, the app gate covers it like any other component.
 */
@Component({
  selector: 'app-agents-markdown',
  standalone: true,
  imports: [NgTemplateOutlet],
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./agents-markdown.component.scss'],
  template: `
    <ng-template #inlineTpl let-nodes>
      @for (node of nodes; track $index) {
        @switch (node.kind) {
          @case ('text') {<ng-container>{{ node.text }}</ng-container>}
          @case ('br') {<br />}
          @case ('code') {<code class="md-code">{{ node.text }}</code>}
          @case ('strong') {<strong><ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: node.children }"></ng-container></strong>}
          @case ('em') {<em><ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: node.children }"></ng-container></em>}
          @case ('link') {<a class="md-link" [href]="node.href" target="_blank" rel="noopener noreferrer"><ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: node.children }"></ng-container></a>}
        }
      }
    </ng-template>

    <ng-template #listTpl let-list>
      @if (list.ordered) {
        <ol class="md-list" [attr.start]="list.start">
          <ng-container *ngTemplateOutlet="itemsTpl; context: { $implicit: list.items }"></ng-container>
        </ol>
      } @else {
        <ul class="md-list">
          <ng-container *ngTemplateOutlet="itemsTpl; context: { $implicit: list.items }"></ng-container>
        </ul>
      }
    </ng-template>

    <ng-template #itemsTpl let-items>
      @for (item of items; track $index) {
        <li>
          <ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: item.inline }"></ng-container>
          @for (child of item.children; track $index) {
            <ng-container *ngTemplateOutlet="listTpl; context: { $implicit: child }"></ng-container>
          }
        </li>
      }
    </ng-template>

    <ng-template #blocksTpl let-blocks>
      @for (block of blocks; track $index) {
        @switch (block.kind) {
          @case ('heading') {
            @switch (block.level) {
              @case (3) {<h3 class="md-h"><ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: block.inline }"></ng-container></h3>}
              @case (4) {<h4 class="md-h"><ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: block.inline }"></ng-container></h4>}
              @default {<h5 class="md-h"><ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: block.inline }"></ng-container></h5>}
            }
          }
          @case ('paragraph') {
            <p class="md-p"><ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: block.inline }"></ng-container></p>
          }
          @case ('code') {
            <div class="md-codeblock">
              @if (block.lang) {
                <div class="md-code-lang">{{ block.lang }}</div>
              }
              <pre class="md-pre"><code>{{ block.text }}</code></pre>
            </div>
          }
          @case ('list') {
            <ng-container *ngTemplateOutlet="listTpl; context: { $implicit: block }"></ng-container>
          }
          @case ('quote') {
            <blockquote class="md-quote">
              <ng-container *ngTemplateOutlet="blocksTpl; context: { $implicit: block.blocks }"></ng-container>
            </blockquote>
          }
          @case ('rule') {
            <hr class="md-rule" />
          }
          @case ('table') {
            <div class="md-table-wrap">
              <table class="md-table">
                <thead>
                  <tr>
                    @for (cell of block.header; track $index) {
                      <th [style.text-align]="block.align[$index]">
                        <ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: cell }"></ng-container>
                      </th>
                    }
                  </tr>
                </thead>
                <tbody>
                  @for (row of block.rows; track $index) {
                    <tr>
                      @for (cell of row; track $index) {
                        <td [style.text-align]="block.align[$index]">
                          <ng-container *ngTemplateOutlet="inlineTpl; context: { $implicit: cell }"></ng-container>
                        </td>
                      }
                    </tr>
                  }
                </tbody>
              </table>
            </div>
          }
        }
      }
    </ng-template>

    <div class="md" data-testid="agents-markdown">
      <ng-container *ngTemplateOutlet="blocksTpl; context: { $implicit: blocks }"></ng-container>
      @if (truncated) {
        <p class="md-p md-truncated" data-testid="agents-markdown-truncated">…</p>
      }
    </div>
  `
})
export class AgentsMarkdownComponent implements OnChanges {
  /** The reply's Markdown. Anything that is not a string draws nothing. */
  @Input() text: string | null | undefined = '';

  blocks: MdBlock[] = [];
  truncated = false;

  ngOnChanges(): void {
    const document = parseAgentsMarkdown(this.text);
    this.blocks = document.blocks;
    this.truncated = document.truncated;
  }
}
